import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { EntryCyclesService, EntryCyclesView } from '../../_generated';
import { EmissionPointsRequestStatusAction } from '../actionstypes/emission-points/emission-points-request-status.actiontype';
import * as EmissionPointsAction from '../actionstypes/emission-points/emission-points.actiontype';
import { EntryCyclesRequestStatusAction } from '../actionstypes/entry-cycles/entry-cycles-request-status.actiontype';
import { EntryCyclesActions } from '../actionstypes/entry-cycles/entry-cycles.actiontype';
import { ParametersRequestStatusAction } from '../actionstypes/parameters/parameters-request-status.actiontype';
import * as ParametersAction from '../actionstypes/parameters/parameters.actiontype';
import { RequestStatusAction } from '../actionstypes/request-status/request-status.actiontype';
import { ShopsRequestStatusAction } from '../actionstypes/shops/shops-request-status.actiontype';
import * as ShopsAction from '../actionstypes/shops/shops.actiontype';
import { SitesRequestStatusAction } from '../actionstypes/sites/sites-request-status.actiontype';
import * as SitesAction from '../actionstypes/sites/sites.actiontype';
import { ToolsRequestStatusAction } from '../actionstypes/tools/tools-request-status.actiontype';
import * as ToolsAction from '../actionstypes/tools/tools.actiontype';
import { DataEntryStore } from '../reducers/data-entry.reducer';
import { getDataType } from '../selectors/data-type.selector';
import { getSelectedEntryCycle } from '../selectors/entry-cycles.selector';

@Injectable()
export class EntryCyclesEffect {
  private readonly store = inject(Store<DataEntryStore>);
  private readonly actions$ = inject(Actions);
  private readonly entryCyclesService = inject(EntryCyclesService);

  loadEntryCycles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryCyclesActions.load),
      exhaustMap(() => {
        return of(new RequestStatusAction(EntryCyclesRequestStatusAction.Load));
      }),
    ),
  );

  requestEntryCycles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryCyclesRequestStatusAction.Load),
      exhaustMap(() =>
        this.entryCyclesService.entryCyclesControllerFindAll(true).pipe(
          map((entryCycles: EntryCyclesView[]) => EntryCyclesActions.get({ entryCycles })),
          catchError((error) => of(new RequestStatusAction(EntryCyclesRequestStatusAction.Error, error as Error))),
        ),
      ),
    ),
  );

  successRequestEntryCycles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntryCyclesActions.get),
      withLatestFrom(this.store.select(getSelectedEntryCycle), this.store.select(getDataType)),
      mergeMap(([, selectedEntryCycle]) => {
        if (selectedEntryCycle) {
          return [
            new RequestStatusAction(EntryCyclesRequestStatusAction.Success),
            new SitesAction.Load(),
            new ShopsAction.Load(),
            new ToolsAction.Load(),
            new EmissionPointsAction.Load(),
            new ParametersAction.Load(),
          ];
        } else {
          return [
            new RequestStatusAction(EntryCyclesRequestStatusAction.Success),
            new RequestStatusAction(SitesRequestStatusAction.Success),
            new RequestStatusAction(ShopsRequestStatusAction.Success),
            new RequestStatusAction(ToolsRequestStatusAction.Success),
            new RequestStatusAction(EmissionPointsRequestStatusAction.Success),
            new RequestStatusAction(ParametersRequestStatusAction.Success),
          ];
        }
      }),
    ),
  );
}
